import { ApiClient } from '../../client';
import { ApiClientOptions } from '../../types';
import {
  Manufacturer,
  CreateManufacturerRequest,
  UpdateManufacturerRequest,
  ManufacturersQueryParams,
  ManufacturerResponse,
  ManufacturerQueryResponse,
  ManufacturerNameVariant,
  ManufacturerNamePrecedenceStep,
  SetManufacturerPreferredNameResponse,
  ClearManufacturerPreferredNameResponse,
  ManufacturerSourcePanel,
  ManufacturerHistoryEvent,
  ManufacturerSimilarResponse,
  ManufacturerLinkCandidate,
  ManufacturerManualLinkRequest,
  ManufacturerManualUnlinkRequest,
  ManufacturerLinkActionResponse,
  ManufacturerLinkedProductsResponse,
  ManufacturerCorporateStructure,
  ManufacturerParentLinkResponse,
  CreateManufacturerStubRelativeRequest,
  CreateManufacturerStubRelativeResponse,
} from './types';
import { convertDatesToStrings } from '../../../../utils/dateConversion';

export class ManufacturerService {
  constructor(private api: ApiClient) {}

  async getAll(params?: ManufacturersQueryParams, config?: ApiClientOptions) {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.advancedSearch)
      queryParams.append(
        'advancedSearch',
        JSON.stringify(params.advancedSearch)
      );
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection)
      queryParams.append('sortDirection', params.sortDirection);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.listMode) queryParams.append('listMode', params.listMode);

    const query = queryParams.toString();
    const url = `/manufacturers${query ? `?${query}` : ''}`;

    return this.api.get<ManufacturerQueryResponse>(url, config);
  }

  async getById(id: number) {
    return this.api.get<Manufacturer>(`/manufacturers/${id}`);
  }

  async create(data: CreateManufacturerRequest) {
    const payload = convertDatesToStrings(data);
    return this.api.post<ManufacturerResponse>('/manufacturers', payload);
  }

  async update(data: UpdateManufacturerRequest) {
    const payload = convertDatesToStrings(data);
    return this.api.put<ManufacturerResponse>(
      `/manufacturers/${data.manufacturer_id}`,
      payload
    );
  }

  async delete(id: number) {
    return this.api.delete(`/manufacturers/${id}`);
  }

  /** GET /api/manufacturers/similar (issue #349, Seq/UPS-005 Sub-task 5.1).
   * `signal` lets a type-ahead caller (e.g. CorporateStructureTab's
   * RelativePicker, issue #654 Seq 16 QA follow-up) abort a superseded
   * request when the user types another character, rather than letting it
   * complete and hold a DB pool slot for a query no one needs anymore. */
  async findSimilar(name: string, maxResults?: number, signal?: AbortSignal) {
    const queryParams = new URLSearchParams({ name });
    if (maxResults) queryParams.append('max_results', maxResults.toString());
    return this.api.get<ManufacturerSimilarResponse>(
      `/manufacturers/similar?${queryParams.toString()}`,
      signal ? { signal } : undefined
    );
  }

  async getNameVariants(id: number) {
    return this.api.get<ManufacturerNameVariant[]>(
      `/manufacturers/${id}/name-variants`
    );
  }

  async getNamePrecedenceConfig() {
    return this.api.get<ManufacturerNamePrecedenceStep[]>(
      '/manufacturers/name-precedence-config'
    );
  }

  async setPreferredName(id: number, name: string) {
    return this.api.put<SetManufacturerPreferredNameResponse>(
      `/manufacturers/${id}/preferred-name`,
      { name }
    );
  }

  async clearPreferredName(id: number) {
    return this.api.delete<ClearManufacturerPreferredNameResponse>(
      `/manufacturers/${id}/preferred-name`
    );
  }

  async getSourceDetail(id: number) {
    return this.api.get<ManufacturerSourcePanel[]>(
      `/manufacturers/${id}/source-detail`
    );
  }

  async getHistory(id: number) {
    return this.api.get<ManufacturerHistoryEvent[]>(
      `/manufacturers/${id}/history`
    );
  }

  /** Sub-task 14.4 (MFGV2-014) — the Corporate Structure panel's read. */
  async getCorporateStructure(id: number) {
    return this.api.get<ManufacturerCorporateStructure>(
      `/manufacturers/${id}/corporate-structure`
    );
  }

  /** Sub-task 14.2 — set/change a manufacturer's parent. */
  async setParent(id: number, parentManufacturerId: number) {
    return this.api.put<ManufacturerParentLinkResponse>(
      `/manufacturers/${id}/parent`,
      { parent_manufacturer_id: parentManufacturerId }
    );
  }

  /** Sub-task 14.2 — remove a manufacturer's parent link. */
  async removeParent(id: number) {
    return this.api.delete<ManufacturerParentLinkResponse>(
      `/manufacturers/${id}/parent`
    );
  }

  /** Sub-task 14.3 — create a stub parent/child and link it in one call. */
  async createStubRelative(request: CreateManufacturerStubRelativeRequest) {
    return this.api.post<CreateManufacturerStubRelativeResponse>(
      `/manufacturers/${request.manufacturerId}/stub-relative`,
      {
        manufacturer_name: request.manufacturer_name,
        relationship: request.relationship,
        lei_code: request.lei_code,
      }
    );
  }

  async searchLinkCandidates(
    source: string,
    q: string,
    manufacturerId: number
  ) {
    return this.api.get<ManufacturerLinkCandidate[]>(
      `/manufacturers/link-sources/${source}/search?q=${encodeURIComponent(q)}&manufacturerId=${manufacturerId}`
    );
  }

  async manualLink(request: ManufacturerManualLinkRequest) {
    return this.api.post<ManufacturerLinkActionResponse>(
      `/manufacturers/${request.manufacturerId}/link-sources/${request.source}`,
      { source_record_id: request.sourceRecordId }
    );
  }

  async manualUnlink(request: ManufacturerManualUnlinkRequest) {
    return this.api.delete<ManufacturerLinkActionResponse>(
      `/manufacturers/${request.manufacturerId}/link-sources/${request.source}/${encodeURIComponent(request.naturalKey)}`,
      { data: { reason: request.reason } }
    );
  }

  async getLinkedProducts(
    manufacturerId: number,
    source: string,
    page: number,
    pageSize: number,
    naturalKey?: string
  ) {
    const naturalKeyParam = naturalKey
      ? `&naturalKey=${encodeURIComponent(naturalKey)}`
      : '';
    return this.api.get<ManufacturerLinkedProductsResponse>(
      `/manufacturers/${manufacturerId}/link-sources/${source}/products?page=${page}&pageSize=${pageSize}${naturalKeyParam}`
    );
  }
}
